<template>
  <div class="bbr-questionnaire-scores">
    <v-row>
      <v-col cols="12">
        <v-card outlined>
          <v-card-title class="text-h6">Upload Score Matrix</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="8">
                <v-file-input
                  v-model="file"
                  :rules="[(v) => !!v || 'File is required']"
                  accept=".xls,.xlsx"
                  label="Score matrix xlsx"
                  show-size
                  outlined
                  flat
                  dense
                  :disabled="loading"
                >
                  <template v-slot:selection="{ text }">
                    <v-chip small label color="primary">{{ text }}</v-chip>
                  </template>
                </v-file-input>
              </v-col>
              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="primary"
                  class="mr-2"
                  :loading="loading"
                  :disabled="!file"
                  @click="validateFile"
                >
                  Validate
                </v-btn>
                <v-btn
                  v-if="canImport"
                  color="success"
                  :loading="loading"
                  @click="confirmImport"
                >
                  Import
                </v-btn>
              </v-col>
            </v-row>

            <v-row v-if="validationErrors.length">
              <v-col>
                <v-simple-table class="red lighten-4" dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Validation Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(err, i) in validationErrors" :key="i">
                        <td>{{ err }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-row v-if="validationWarnings.length">
              <v-col>
                <v-simple-table class="yellow lighten-4" dense>
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th class="text-left">Warnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(w, i) in validationWarnings" :key="i">
                        <td>{{ w }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>

            <v-row v-if="validationPreview && validationPreview.questions">
              <v-col>
                <h3 class="mt-4 mb-2">Validation Preview</h3>
                <p class="text-body-2 grey--text">
                  Review the parsed matrix below. On import, existing scores for
                  these {{ validationPreview.questions.length }} question(s)
                  will be replaced.
                </p>
                <questionnaire-score-matrix :questions="previewQuestions" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col cols="12">
        <h2 class="mb-3">Current Score Matrix</h2>
        <v-progress-linear v-if="loadingMatrix" indeterminate color="primary" />
        <questionnaire-score-matrix :questions="matrix" />
      </v-col>
    </v-row>

    <confirm-dialog ref="confirm" />
    <v-toast ref="toast" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { flattenDeep, values } from 'lodash'
import VToast from '@/components/elements/Toast'
import ConfirmDialog from '@/components/modals/ConfirmDialog'
import QuestionnaireScoreMatrix from './components/QuestionnaireScoreMatrix'

export default {
  name: 'QuestionnaireScoresPage',

  components: {
    VToast,
    ConfirmDialog,
    QuestionnaireScoreMatrix,
  },

  data() {
    return {
      file: null,
      loading: false,
      loadingMatrix: false,
    }
  },

  computed: {
    ...mapState('questionnaireScores', {
      matrix: (s) => s.matrix,
      validationPreview: (s) => s.validationPreview,
      validationErrors: (s) => s.validationErrors,
      validationWarnings: (s) => s.validationWarnings,
    }),

    canImport() {
      return (
        this.validationPreview &&
        Array.isArray(this.validationPreview.questions) &&
        this.validationPreview.questions.length > 0 &&
        this.validationErrors.length === 0
      )
    },

    previewQuestions() {
      if (!this.validationPreview) return []
      return (this.validationPreview.questions || []).map((q) => ({
        ...q,
        unresolved_summary: Array.from(
          new Set((q.rows || []).flatMap((r) => r.unresolved || []))
        ),
      }))
    },
  },

  async mounted() {
    await this.refreshMatrix()
  },

  methods: {
    ...mapActions('questionnaireScores', [
      'fetchMatrix',
      'validateUpload',
      'applyUpload',
    ]),

    async refreshMatrix() {
      this.loadingMatrix = true
      try {
        await this.fetchMatrix()
      } catch (e) {
        this.showError(e, 'Failed to load existing scores.')
      } finally {
        this.loadingMatrix = false
      }
    },

    buildFormData() {
      const fd = new FormData()
      fd.append('excel_file', this.file)
      return fd
    },

    async validateFile() {
      if (!this.file) return
      this.loading = true
      try {
        await this.validateUpload(this.buildFormData())
        this.$refs.toast.open({
          color: 'primary',
          message: this.validationErrors.length
            ? 'File parsed with errors. Fix them and re-validate.'
            : 'File parsed successfully. Review and click Import.',
        })
      } catch (e) {
        this.showError(e, 'Validation failed.')
      } finally {
        this.loading = false
      }
    },

    async confirmImport() {
      const count = this.validationPreview?.questions?.length || 0
      const ok = await this.$refs.confirm.open(
        'Replace existing scores?',
        `This will wipe and rebuild questionnaire_option_scores for ${count} question(s). Continue?`
      )
      if (!ok) return
      await this.importFile()
    },

    async importFile() {
      this.loading = true
      try {
        const res = await this.applyUpload(this.buildFormData())
        this.$refs.toast.open({
          color: 'primary',
          message: res.message || 'Imported successfully.',
        })
        this.file = null
      } catch (e) {
        this.showError(e, 'Import failed.')
      } finally {
        this.loading = false
      }
    },

    showError(err, fallback) {
      const message =
        (err.response &&
          err.response.data &&
          (err.response.data.message ||
            (err.response.data.errors &&
              flattenDeep(values(err.response.data.errors)).join(' ')))) ||
        fallback
      this.$refs.toast.open({
        color: 'red',
        timeout: 5000,
        message,
      })
    },
  },
}
</script>
